import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, withCtx as _withCtx, isRef as _isRef, createBlock as _createBlock } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = {
  key: 1,
  class: "mt-3"
}
const _hoisted_3 = { key: 2 }
const _hoisted_4 = { key: 3 }
const _hoisted_5 = { class: "mt-2" }
const _hoisted_6 = {
  key: 0,
  class: "mt-4"
}
const _hoisted_7 = {
  key: 1,
  class: "mt-4"
}
const _hoisted_8 = {
  key: 2,
  class: "mt-3"
}
const _hoisted_9 = { key: 4 }
const _hoisted_10 = {
  key: 4,
  class: "mt-4"
}
const _hoisted_11 = {
  key: 0,
  class: "mt-4"
}
const _hoisted_12 = {
  key: 2,
  class: "mt-4"
}
const _hoisted_13 = { key: 4 }

import { ref, computed } from 'vue';
import { BAlert, BButton, BModal, BSpinner } from 'bootstrap-vue-next';
import { useUserStore } from '@/store/user';
import { useQuery } from '@vue/apollo-composable';
import {
  OrganizationDocument,
  UserOrganizationAssociationsDocument,
  OrganizationType,
  OrganizationUserAssociationType,
} from '@/gql/graphql';
import OrganizationForm from '@/components/organization/OrganizationForm.vue';
import OrganizationMembershipList from '@/components/organization/OrganizationMembershipList.vue';
import InviteCreate from '@/components/invite/InviteCreate.vue';
import InviteList from '@/components/invite/InvitesList.vue';
import { ORGANIZATION_USER_ROLES } from '@/constants';
import { deepParseDates } from '@/utils/date-utils';

// user store to get user ID or token-based identity

export default /*@__PURE__*/_defineComponent({
  __name: 'OrganizationView',
  setup(__props) {

const userStore = useUserStore();

// invites counts
const inviteCountIncoming = ref(0);
const inviteCountOutgoing = ref(0);

// invites refs
const incomingInvitesListRef = ref<InstanceType<typeof InviteList> | null>(
  null
);
const outgoingInvitesListRef = ref<InstanceType<typeof InviteList> | null>(
  null
);

// refs to handle the modals
const selectedOrgAssoc = ref<OrganizationUserAssociationType | null>(null);
const showOrgDetails = ref(false);

// Show organization details modal
function showOrganizationDetails(
  assoc: OrganizationUserAssociationType,
  index: number,
  event: Event
) {
  selectedOrgAssoc.value = assoc;
  showOrgDetails.value = true;
}

// track org-user association mutation progress
const showInviteCreate = ref(false);

// --- Query user`s organization, user may be owner, member or admin. ---
const {
  result: userOrganizationResult,
  loading: loadingUserOrganization,
  error: errorUserOrganization,
  refetch: refetchOrganization,
} = useQuery(OrganizationDocument);

const userOrgData = computed<OrganizationType | null>(() => {
  console.debug('fetched org:', userOrganizationResult.value?.organization);
  if (userOrganizationResult.value?.organization) {
    return {
      ...(deepParseDates(
        userOrganizationResult.value.organization
      ) as OrganizationType),
    };
  }
  return null;
});

// --- Query organization-user associations ---
const userId = computed(() => userStore.id || null);

// Then define the user-organization associations queries
const {
  result: userAssocResult,
  loading: loadingUserAssoc,
  error: errorUserAssoc,
  refetch: refetchUserAssoc,
} = useQuery(
  UserOrganizationAssociationsDocument,
  () => ({
    userId: Number(userId.value),
  }),
  {
    enabled: computed(() => Boolean(userId.value)),
  }
);

const userAssociations = computed<OrganizationUserAssociationType[]>(() => {
  const rawList = userAssocResult.value?.userOrganizationAssociations || [];
  const parsedList = rawList.map((assoc) => ({
    ...(deepParseDates(assoc) as OrganizationUserAssociationType),
  }));
  console.debug('User associations:', parsedList);
  return parsedList;
});

// --- Organizations "owner" or "admin" can edit ---
const canEditOrg = computed(() => {
  if (!userOrgData.value) {
    return false;
  }
  if (
    userAssociations.value.some(
      (assoc) =>
        assoc.organization?.id === userOrgData.value?.id &&
        (assoc.userRole === ORGANIZATION_USER_ROLES.OWNER ||
          assoc.userRole === ORGANIZATION_USER_ROLES.ADMIN) &&
        !assoc.removedAt
    )
  ) {
    return true;
  }
  return false;
});

/* The array of associations for the user, from orgAssocResult */
const organizationAssociations = computed<OrganizationUserAssociationType[]>(
  () => {
    //const rawList = orgAssocResult.value?.organizationUserAssociations || [];
    const rawList = userOrgData.value?.usersAssociated || [];
    const parsedList = rawList.map((assoc) => ({
      ...(assoc as OrganizationUserAssociationType),
    }));
    console.debug('Organization associations:', parsedList);
    return parsedList;
  }
);

// Membership is "owner", "admin" or "member"
const membership = computed(() => {
  if (!userOrgData.value) return null;
  const assoc = userAssociations.value.find(
    (assoc) => assoc.organization?.id === userOrgData.value?.id
  );
  if (!assoc) return null;
  return assoc.userRole;
});

const noMembership = computed(() => {
  // return membership.value === null;
  return userStore.noOrganizationSelected;
});

// --- Handlers ---
async function handleOrganizationSaved() {
  await requestRefetchOrganization();
  await requestRefetchUserAssoc();
  await userStore.fetchUserDetails();
}

async function requestRefetchOrganization() {
  await refetchOrganization()
    ?.catch((err) => {
      console.error('Refetch organization failed:', err);
    })
    ?.then(() => {
      console.debug('Refetched organization');
    });
}

async function requestRefetchUserAssoc() {
  await refetchUserAssoc()
    ?.catch((err) => {
      console.error('Refetch user-organization associations failed:', err);
    })
    ?.then(() => {
      console.debug('Refetched user-organization associations');
    });
}

// --- handle component emits and necessary updates ---
function updateNumberOfIncomingInvites(count: number) {
  inviteCountIncoming.value = count;
  userStore.setNumberOfPendingInvites(count);
}

function updateNumberOfOutgoingInvites(count: number) {
  inviteCountOutgoing.value = count;
}

async function handleMembershipsUpdate() {
  await requestRefetchOrganization();
  await requestRefetchUserAssoc();
}

function handleIncomingInvitesUpdated() {
  incomingInvitesListRef.value?.requestSearch();
  handleMembershipsUpdate();
}

function handleOutgoingInvitesUpdated() {
  outgoingInvitesListRef.value?.requestSearch();
}

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("h1", null, [
      _createVNode(_component_font_awesome_icon, {
        icon: ['fas', 'building-user'],
        class: "me-2"
      }),
      _cache[13] || (_cache[13] = _createTextVNode(" Organization Settings ", -1))
    ]),
    (membership.value && userOrgData.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_1, [
          _cache[14] || (_cache[14] = _createTextVNode(" You are currently acting as ", -1)),
          _createElementVNode("strong", null, _toDisplayString(membership.value), 1),
          _cache[15] || (_cache[15] = _createTextVNode(" of the ", -1)),
          _createElementVNode("strong", null, _toDisplayString(userOrgData.value.name), 1),
          _cache[16] || (_cache[16] = _createTextVNode(" organization. ", -1))
        ]))
      : _createCommentVNode("", true),
    _createVNode(_unref(BAlert), {
      modelValue: _unref(errorUserOrganization),
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(errorUserOrganization) ? (errorUserOrganization).value = $event : null)),
      variant: "danger",
      dismissible: "",
      class: "mt-3"
    }, {
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(_unref(errorUserOrganization)) + " ", 1),
        _createVNode(_unref(BButton), {
          onClick: requestRefetchOrganization,
          variant: "outline-dark",
          class: "btn-sm"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_font_awesome_icon, { icon: ['fas', 'sync'] }),
            _cache[17] || (_cache[17] = _createTextVNode(" Retry", -1))
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    (_unref(loadingUserOrganization))
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createVNode(_unref(BSpinner), {
            small: "",
            label: "Loading organization..."
          })
        ]))
      : _createCommentVNode("", true),
    (_unref(userStore).noOrganizationSelected && _unref(userStore).admin)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createVNode(_unref(BAlert), {
            variant: "info",
            class: "mt-3",
            modelValue: noMembership.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((noMembership).value = $event))
          }, {
            default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
              _createTextVNode(" You are not a member of any organization. Provide your email address to your organization admin and ask them to add you to their organization or create a brand new organization below since you are an admin. ", -1)
            ]))]),
            _: 1
          }, 8, ["modelValue"]),
          _createVNode(OrganizationForm, {
            headerText: 'Create new organization',
            editable: true,
            buttonText: 'Create new organization',
            showAdminFields: false,
            onOrganizationCreated: handleOrganizationSaved
          })
        ]))
      : (_unref(userStore).noOrganizationSelected)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _cache[20] || (_cache[20] = _createElementVNode("p", null, " You are not a member of any organization. Provide your email address to your organization admin and ask them to add you to their organization or ask your Sampling Human account manager for assistance. ", -1)),
            _createVNode(_unref(BAlert), {
              variant: "warning",
              class: "mt-3",
              modelValue: noMembership.value,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((noMembership).value = $event))
            }, {
              default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
                _createElementVNode("h5", null, "You are not a member of any organization.", -1),
                _createElementVNode("p", null, " Provide your email address to your organization admin and ask them to add you to their organization or ask your Sampling Human account manager for assistance. ", -1)
              ]))]),
              _: 1
            }, 8, ["modelValue"])
          ]))
        : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("h4", null, [
        _createVNode(_component_font_awesome_icon, { icon: ['fas', 'city'] }),
        _cache[21] || (_cache[21] = _createTextVNode(" List of your Organizations ", -1))
      ]),
      _createVNode(_unref(BAlert), {
        modelValue: _unref(errorUserAssoc),
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (_isRef(errorUserAssoc) ? (errorUserAssoc).value = $event : null)),
        variant: "danger",
        dismissible: "",
        class: "mt-3"
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref(errorUserAssoc)) + " ", 1),
          _createVNode(_unref(BButton), {
            onClick: requestRefetchUserAssoc,
            variant: "outline-dark",
            class: "btn-sm"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_font_awesome_icon, { icon: ['fas', 'sync'] }),
              _cache[22] || (_cache[22] = _createTextVNode(" Retry", -1))
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"]),
      (inviteCountIncoming.value > 0)
        ? (_openBlock(), _createElementBlock("h5", _hoisted_6, " Your pending invitations "))
        : _createCommentVNode("", true),
      _createVNode(InviteList, {
        ref_key: "incomingInvitesListRef",
        ref: incomingInvitesListRef,
        "email-pattern": _unref(userStore).email,
        onCountUpdated: _cache[4] || (_cache[4] = ($event: any) => (updateNumberOfIncomingInvites($event))),
        onInvitesUpdated: _cache[5] || (_cache[5] = ($event: any) => (handleIncomingInvitesUpdated())),
        "status-pattern": "PENDING"
      }, null, 8, ["email-pattern"]),
      (inviteCountIncoming.value > 0)
        ? (_openBlock(), _createElementBlock("h5", _hoisted_7, "Your organizations"))
        : _createCommentVNode("", true),
      (_unref(loadingUserAssoc))
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createVNode(_unref(BSpinner), {
              small: "",
              label: "Loading organizations..."
            })
          ]))
        : _createCommentVNode("", true),
      (userAssociations.value)
        ? (_openBlock(), _createBlock(OrganizationMembershipList, {
            key: 3,
            isLoading: _unref(loadingUserAssoc),
            isError: _unref(errorUserAssoc) !== null,
            associations: userAssociations.value,
            canManage: canEditOrg.value,
            "show-active-status": true,
            showEmail: false,
            showOrganization: true,
            isOwner: membership.value === _unref(ORGANIZATION_USER_ROLES).OWNER,
            onMembershipsUpdated: handleMembershipsUpdate,
            onRowClicked: showOrganizationDetails
          }, null, 8, ["isLoading", "isError", "associations", "canManage", "isOwner"]))
        : _createCommentVNode("", true),
      (userAssociations.value && userAssociations.value.length === 0)
        ? (_openBlock(), _createElementBlock("p", _hoisted_9, " No organizations found. "))
        : _createCommentVNode("", true)
    ]),
    (canEditOrg.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
          _createElementVNode("h4", null, [
            _createVNode(_component_font_awesome_icon, {
              icon: ['fas', 'users'],
              class: "me-1"
            }),
            _createTextVNode(" Manage members of " + _toDisplayString(userOrgData.value?.name), 1)
          ]),
          (inviteCountOutgoing.value > 0)
            ? (_openBlock(), _createElementBlock("h5", _hoisted_11, " Pending invitations sent by you "))
            : _createCommentVNode("", true),
          _createVNode(InviteList, {
            ref_key: "outgoingInvitesListRef",
            ref: outgoingInvitesListRef,
            inviterUserId: _unref(userStore).id,
            onCountUpdated: _cache[6] || (_cache[6] = ($event: any) => (updateNumberOfOutgoingInvites($event))),
            onInvitesUpdated: _cache[7] || (_cache[7] = ($event: any) => (handleOutgoingInvitesUpdated())),
            "status-pattern": "PENDING"
          }, null, 8, ["inviterUserId"]),
          _createVNode(_unref(BButton), {
            type: "submit",
            variant: "outline-dark",
            class: "mt-2",
            onClick: _cache[8] || (_cache[8] = ($event: any) => (showInviteCreate.value = true))
          }, {
            default: _withCtx(() => [
              _createVNode(_component_font_awesome_icon, {
                icon: ['fas', 'user-plus'],
                class: "me-1"
              }),
              _cache[23] || (_cache[23] = _createTextVNode(" Invite new user ", -1))
            ]),
            _: 1
          }),
          (userOrgData.value)
            ? (_openBlock(), _createBlock(InviteCreate, {
                key: 1,
                show: showInviteCreate.value,
                organization: userOrgData.value,
                onSuccess: _cache[9] || (_cache[9] = ($event: any) => (handleOutgoingInvitesUpdated())),
                onClose: _cache[10] || (_cache[10] = ($event: any) => (showInviteCreate.value = false))
              }, null, 8, ["show", "organization"]))
            : _createCommentVNode("", true),
          (inviteCountOutgoing.value > 0)
            ? (_openBlock(), _createElementBlock("h5", _hoisted_12, "Organization members"))
            : _createCommentVNode("", true),
          (userOrgData.value)
            ? (_openBlock(), _createBlock(OrganizationMembershipList, {
                key: 3,
                isLoading: _unref(loadingUserOrganization),
                isError: _unref(errorUserOrganization) !== null,
                associations: organizationAssociations.value,
                canManage: canEditOrg.value,
                isOwner: membership.value === _unref(ORGANIZATION_USER_ROLES).OWNER,
                "show-active-status": false,
                showEmail: true,
                showOrganization: false,
                onMembershipsUpdated: handleMembershipsUpdate,
                class: "mt-3"
              }, null, 8, ["isLoading", "isError", "associations", "canManage", "isOwner"]))
            : _createCommentVNode("", true),
          (organizationAssociations.value && organizationAssociations.value.length === 0)
            ? (_openBlock(), _createElementBlock("p", _hoisted_13, " No organization members found. "))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    (selectedOrgAssoc.value?.organization)
      ? (_openBlock(), _createBlock(_unref(BModal), {
          key: 5,
          id: "orgDetailsModal",
          modelValue: showOrgDetails.value,
          "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((showOrgDetails).value = $event)),
          "hide-footer": ""
        }, {
          title: _withCtx(() => [
            _createVNode(_component_font_awesome_icon, {
              icon: ['fas', 'building'],
              class: "me-1"
            }),
            _createTextVNode(" " + _toDisplayString(selectedOrgAssoc.value.organization.name), 1)
          ]),
          default: _withCtx(() => [
            _createVNode(OrganizationForm, {
              headerText: "",
              organization: selectedOrgAssoc.value.organization,
              editable: 
          [
            _unref(ORGANIZATION_USER_ROLES).OWNER,
            _unref(ORGANIZATION_USER_ROLES).ADMIN,
          ].includes(selectedOrgAssoc.value.userRole as any)
        ,
              buttonText: 'Save Changes',
              showAdminFields: false,
              onOrganizationUpdated: handleOrganizationSaved,
              onClose: _cache[11] || (_cache[11] = ($event: any) => {
          showOrgDetails.value = false;
          selectedOrgAssoc.value = null;
        })
            }, null, 8, ["organization", "editable"])
          ]),
          _: 1
        }, 8, ["modelValue"]))
      : _createCommentVNode("", true)
  ]))
}
}

})